export default function ReportSection() {
  const steps = [
    { icon: 'ri-calendar-check-line', title: '作業当日', desc: 'スタッフが訪問し、ご依頼の作業を丁寧に実施します。' },
    { icon: 'ri-camera-line', title: '作業中に撮影', desc: '作業前・作業中・完了後の写真を撮影。気になる箇所も記録します。' },
    { icon: 'ri-chat-1-line', title: 'LINEで即報告', desc: '作業完了後すぐに、写真付きのレポートをLINEでお送りします。' },
    { icon: 'ri-group-line', title: 'ご家族も確認', desc: '離れて暮らすご家族のLINEにも共有。実家の様子がすぐわかります。' },
  ];

  return (
    <section id="report" aria-label="LINE報告システムのご紹介" className="py-20 md:py-28 bg-stone-800">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-amber-400 text-sm font-semibold tracking-widest mb-3 uppercase">
            安心の報告システム
          </span>
          <h2 className="text-2xl md:text-4xl font-black text-white leading-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            「今日も無事でした」が<br />
            <span className="text-amber-300">スマホで届く</span>
          </h2>
          <p className="mt-4 text-stone-300 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            作業後は必ず写真付きでLINE報告。離れて暮らすご家族も、実家の状態をリアルタイムで確認できます。
          </p>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-10 items-center">
          {/* Phone Mockup */}
          <div className="w-full lg:w-2/5 flex justify-center">
            <div className="relative w-64 md:w-72">
              {/* Phone Frame */}
              <div className="bg-stone-900 rounded-3xl p-3 border-2 border-stone-600 shadow-2xl">
                <div className="bg-white rounded-2xl overflow-hidden">
                  {/* LINE Header */}
                  <div className="bg-green-500 px-4 py-3 flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-white rounded-full">
                      <i className="ri-home-smile-line text-green-500 text-sm"></i>
                    </div>
                    <div>
                      <div className="text-white text-xs font-bold">家の管理おまかせパック</div>
                      <div className="text-green-100 text-xs">オンライン</div>
                    </div>
                  </div>
                  {/* Chat Messages */}
                  <div className="bg-green-50 p-3 space-y-3 min-h-64">
                    <div className="flex gap-2">
                      <div className="w-7 h-7 flex items-center justify-center bg-amber-500 rounded-full shrink-0">
                        <i className="ri-user-line text-white text-xs"></i>
                      </div>
                      <div className="bg-white rounded-lg rounded-tl-none p-2.5 max-w-48 shadow-sm">
                        <p className="text-xs text-stone-700 leading-relaxed">本日の作業が完了しました！</p>
                        <div className="mt-2 rounded overflow-hidden">
                          <img
                            src="https://public.readdy.ai/ai/img_res/4f1104058b7b7d6b918cc1354c987517.jpg"
                            alt="作業後の庭"
                            className="w-full h-20 object-cover object-top"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <p className="text-xs text-stone-500 mt-1">庭の草刈り・剪定完了</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-7 h-7 flex items-center justify-center bg-amber-500 rounded-full shrink-0">
                        <i className="ri-user-line text-white text-xs"></i>
                      </div>
                      <div className="bg-white rounded-lg rounded-tl-none p-2.5 max-w-48 shadow-sm">
                        <p className="text-xs text-stone-700 leading-relaxed">
                          ⚠️ 北側の雨樋に少し詰まりが見られました。次回確認いたします。
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-green-400 rounded-lg rounded-tr-none p-2.5 max-w-40 shadow-sm">
                        <p className="text-xs text-white leading-relaxed">ありがとうございます！安心しました😊</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {steps.map((s, i) => (
                <div key={i} className="bg-stone-700 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-full">
                      <i className={`${s.icon} text-amber-400 text-lg`}></i>
                    </div>
                    <div>
                      <span className="text-amber-400 text-xs font-bold">STEP {i + 1}</span>
                      <h3 className="text-white font-bold text-sm">{s.title}</h3>
                    </div>
                  </div>
                  <p className="text-stone-300 text-xs md:text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div className="mt-6 bg-stone-700/50 border border-stone-600 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-full shrink-0 text-white font-bold text-sm">
                  田
                </div>
                <div>
                  <p className="text-stone-200 text-sm leading-relaxed italic">
                    「東京に住んでいる私でも、LINEで実家の様子がわかるので本当に助かっています。母も『写真が届くと安心する』と喜んでいます。」
                  </p>
                  <span className="text-stone-400 text-xs mt-2 block">田中様（50代・東京在住、ご両親が岡山市在住）</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
